// Shopify Wishlist Logic with 6-Month (180 Days) Storage Validity
const WISHLIST_LOCAL_STORAGE_KEY = 'tlf_wishlist';
const WISHLIST_EXPIRY_MS = 180 * 24 * 60 * 60 * 1000; // 6 months in milliseconds

// Helper: Get wishlist handles from localStorage (with 6-month validity check)
function getWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_LOCAL_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);

    // Support legacy simple array format
    if (Array.isArray(parsed)) {
      saveWishlist(parsed); // Automatically migrate to timestamped format
      return parsed;
    }

    // Check if expired (older than 6 months)
    if (parsed.timestamp && (Date.now() - parsed.timestamp > WISHLIST_EXPIRY_MS)) {
      localStorage.removeItem(WISHLIST_LOCAL_STORAGE_KEY);
      return [];
    }

    return parsed.items || [];
  } catch (e) {
    console.error('Error reading wishlist from localStorage:', e);
    return [];
  }
}

// Helper: Save wishlist to localStorage with timestamp
function saveWishlist(list) {
  try {
    const data = {
      items: list,
      timestamp: Date.now()
    };
    localStorage.setItem(WISHLIST_LOCAL_STORAGE_KEY, JSON.stringify(data));
    // Trigger custom event for external components to listen to
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: list }));
  } catch (e) {
    console.error('Error saving wishlist to localStorage:', e);
  }
}

// Check if a product handle is in the wishlist
function isInWishlist(handle) {
  return getWishlist().includes(handle);
}

// Toggle a product handle in the wishlist
function toggleWishlist(handle) {
  if (!handle) return;
  let list = getWishlist();
  const index = list.indexOf(handle);
  
  if (index > -1) {
    list.splice(index, 1); // Remove
  } else {
    list.push(handle); // Add
  }
  
  saveWishlist(list);
  updateWishlistUI();

  // If on Wishlist Page, refresh products dynamically without reloading page
  const gridContainer = document.getElementById('wishlist-grid');
  if (gridContainer) {
    const remaining = getWishlist();
    const emptyState = document.getElementById('wishlist-empty-state');
    
    // Find and remove the specific product card from DOM instantly
    const targetCardBtn = gridContainer.querySelector(`[data-product-handle="${handle}"]`);
    if (targetCardBtn) {
      const cardEl = targetCardBtn.closest('product-component') || targetCardBtn.closest('.contains-card') || targetCardBtn.closest('.shopify-section');
      if (cardEl) {
        cardEl.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
        cardEl.style.opacity = '0';
        cardEl.style.transform = 'scale(0.95)';
        setTimeout(() => {
          cardEl.remove();
          if (remaining.length === 0) {
            gridContainer.innerHTML = '';
            if (emptyState) emptyState.classList.remove('hidden');
          }
        }, 250);
      } else {
        if (remaining.length === 0) {
          gridContainer.innerHTML = '';
          if (emptyState) emptyState.classList.remove('hidden');
        }
      }
    } else {
      loadWishlistPage();
    }
  }
}

// Update all wishlist buttons & header badge on the page
function updateWishlistUI() {
  const list = getWishlist();
  const count = list.length;
  
  // 1. Update Header Badge Count
  const badge = document.getElementById('wishlist-count-bubble');
  const countText = document.getElementById('wishlist-count-text');
  
  if (badge && countText) {
    countText.textContent = count;
    if (count > 0) {
      badge.style.display = 'flex';
    } else {
      badge.style.display = 'none';
    }
  }
  
  // 2. Update all toggle buttons on page
  const toggleButtons = document.querySelectorAll('.wishlist-toggle-btn');
  toggleButtons.forEach(button => {
    const handle = button.getAttribute('data-product-handle');
    const emptyIcon = button.querySelector('.wishlist-icon-empty');
    const filledIcon = button.querySelector('.wishlist-icon-filled');
    if (!emptyIcon || !filledIcon) return;

    if (isInWishlist(handle)) {
      button.classList.add('is-active');
      emptyIcon.classList.add('hidden');
      filledIcon.classList.remove('hidden');
    } else {
      button.classList.remove('is-active');
      emptyIcon.classList.remove('hidden');
      filledIcon.classList.add('hidden');
    }
  });
}

// Shopify Money Formatter
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || window.moneyFormat || '${{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision === undefined ? 2 : precision;
    thousands = thousands === undefined ? ',' : thousands;
    decimal = decimal === undefined ? '.' : decimal;

    if (isNaN(number) || number == null) return 0;

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)?.[1] || 'amount') {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

// Render the Wishlist Page items
async function loadWishlistPage() {
  const gridContainer = document.getElementById('wishlist-grid');
  const emptyState = document.getElementById('wishlist-empty-state');
  
  if (!gridContainer) return; // Not on wishlist page
  
  const list = getWishlist();
  
  if (list.length === 0) {
    if (emptyState) emptyState.classList.remove('hidden');
    gridContainer.innerHTML = '';
    return;
  }
  
  if (emptyState) emptyState.classList.add('hidden');
  
  try {
    // Fetch product card section HTML for all handles in parallel
    const fetchPromises = list.map(handle => 
      fetch(`/products/${handle}?section_id=wishlist-card-render`)
        .then(res => {
          if (!res.ok) throw new Error(`Failed to load ${handle}`);
          return res.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const productComp = doc.querySelector('product-component');
          if (productComp) {
            return productComp.outerHTML;
          }
          const sectionEl = doc.querySelector('.shopify-section') || doc.body;
          return sectionEl.innerHTML.trim();
        })
        .catch(err => {
          console.error(err);
          return null; // Return null on failure
        })
    );
    
    const cardHtmls = (await Promise.all(fetchPromises)).filter(h => h !== null && h !== '');
    
    if (cardHtmls.length === 0) {
      if (emptyState) emptyState.classList.remove('hidden');
      gridContainer.innerHTML = '';
      return;
    }
    
    // Render wishlist product cards
    gridContainer.innerHTML = cardHtmls.join('');
    updateWishlistUI();
    
  } catch (error) {
    console.error('Error rendering wishlist grid:', error);
    gridContainer.innerHTML = `<p class="text-red-500 text-center py-8">An error occurred loading the wishlist. Please try again.</p>`;
  }
}

// Global Click Delegation for Wishlist buttons
document.addEventListener('click', function(e) {
  const btn = e.target.closest('.wishlist-toggle-btn, .wishlist-remove-btn');
  if (btn) {
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.getAttribute('data-product-handle');
    toggleWishlist(handle);
  }
});

// Run updates on DOM content loaded
document.addEventListener('DOMContentLoaded', () => {
  updateWishlistUI();
  loadWishlistPage();
});

// Set up MutationObserver to check for newly added wishlist buttons
const wishlistObserver = new MutationObserver((mutations) => {
  let shouldUpdate = false;
  mutations.forEach(mutation => {
    if (mutation.addedNodes.length > 0) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.classList.contains('wishlist-toggle-btn') || node.querySelector('.wishlist-toggle-btn')) {
            shouldUpdate = true;
          }
        }
      });
    }
  });
  if (shouldUpdate) {
    updateWishlistUI();
  }
});

wishlistObserver.observe(document.body, { childList: true, subtree: true });
